import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Zap } from "lucide-react";
import { HazardStripe } from "@/components/gb/hazard-stripe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, apiBaseUrl, errorMessage } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

type Mode = "login" | "register";

/**
 * Sign-in gate. The console is a dark-chrome product, so the unauthenticated
 * screen drops the shell and centres a single white panel on `chrome-deep` —
 * hazard stripe across its top edge and the sidebar brand block (38px gradient
 * tile, Oswald wordmark, letter-spaced sub-label) stacked above the form.
 */
export function LoginScreen() {
  const { signIn } = useAuth();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("demo@groundbrain.app");
  const [password, setPassword] = useState("demo-password-123");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === "login") {
        const res = await api.auth.login({ body: { email, password } });
        if (res.status !== 200) throw new Error(errorMessage(res));
        return res.body;
      }
      const res = await api.auth.register({
        body: { email, password, name: name.trim() || undefined },
      });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: (body) => signIn(body),
    onError: (e: Error) => setError(e.message),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-chrome-deep px-6 py-12">
      <div className="w-full max-w-[420px] overflow-hidden rounded-panel border-[1.5px] border-border bg-surface shadow-modal">
        <HazardStripe height={4} />

        <div className="px-8 pt-7 pb-8">
          {/* Brand block — the sidebar's tile, wordmark and sub-label. */}
          <div className="flex flex-col items-center text-center">
            <div className="flex size-[38px] shrink-0 items-center justify-center rounded-tile bg-linear-to-br from-brand to-brand-hover text-white">
              <Zap size={20} aria-hidden />
            </div>
            <div className="mt-[11px] font-display text-[1.28rem] leading-none font-bold tracking-[0.02em] text-ink uppercase">
              GroundBrain
            </div>
            <div className="mt-[3px] text-[0.66rem] font-semibold tracking-[0.14em] text-brand uppercase">
              Office Console
            </div>
          </div>

          <form className="mt-7" onSubmit={submit}>
            <h1 className="font-display text-[1.3rem] font-bold tracking-[0.01em] text-ink uppercase">
              {mode === "login" ? "Sign in" : "Create account"}
            </h1>
            <p className="mt-1 text-[0.88rem] text-muted">
              {mode === "login"
                ? "Sign in to continue."
                : "Set up access for your site team."}
            </p>

            <div className="mt-[22px] flex flex-col gap-[14px]">
              {mode === "register" && (
                <div>
                  <label
                    htmlFor="login-name"
                    className="mb-2 block font-display text-[0.78rem] font-medium tracking-[0.04em] text-muted uppercase"
                  >
                    Name (optional)
                  </label>
                  <Input
                    id="login-name"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="login-email"
                  className="mb-2 block font-display text-[0.78rem] font-medium tracking-[0.04em] text-muted uppercase"
                >
                  Email
                </label>
                <Input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="mb-2 block font-display text-[0.78rem] font-medium tracking-[0.04em] text-muted uppercase"
                >
                  Password
                </label>
                <Input
                  id="login-password"
                  type="password"
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <p
                role="alert"
                className="mt-4 rounded-input border-[1.5px] border-danger/40 bg-danger-bg px-[14px] py-[11px] text-[0.86rem] text-danger"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="mt-[18px] w-full"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2
                    size={17}
                    className="animate-spin-slow"
                    aria-hidden
                  />
                  Working…
                </>
              ) : mode === "login" ? (
                "Sign in"
              ) : (
                "Create account"
              )}
            </Button>
          </form>

          <div className="mt-[18px] border-t-[1.5px] border-border-inner pt-[14px]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                setError(null);
                setMode(mode === "login" ? "register" : "login");
              }}
            >
              {mode === "login"
                ? "No account? Register"
                : "Already have an account? Sign in"}
            </Button>

            <p className="mt-3 text-center text-[0.72rem] text-faint">
              <span className="font-display font-semibold tracking-[0.1em] uppercase">
                API
              </span>{" "}
              {apiBaseUrl}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
